import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Composeur de pack à paliers.
 * 1 carte = 1 variante (parfum). Sélection +/- → paliers de remise.
 * Ajout panier en 1 POST items[] via le moteur Horizon (CartLinesUpdateEvent → drawer s'ouvre).
 */
class PackBuilder extends HTMLElement {
  constructor() {
    super();
    /** @type {Map<string, number>} variantId → quantité */
    this.selections = new Map();
  }

  connectedCallback() {
    this.productId = this.dataset.productId;
    this.maxTotal = parseInt(this.dataset.maxTotal, 10) || 0;
    this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'EUR';
    this.flavorSingular = this.dataset.flavorSingular || 'saveur';
    this.flavorPlural = this.dataset.flavorPlural || 'saveurs';
    this.perUnitText = this.dataset.perUnitText || '/sachet';
    this.freeShipThreshold = parseInt(this.dataset.freeShipThreshold || '0', 10);
    this.freeShipText = this.dataset.freeShipText || 'Livraison offerte';

    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]');
    } catch (e) {
      this.tiers = [];
    }
    // Trier les paliers par quantité croissante
    this.tiers.sort((a, b) => a.qty - b.qty);

    this.refs = {
      progressBar: this.querySelector('[ref="progressBar"]'),
      progressLabel: this.querySelector('[ref="progressLabel"]'),
      progressMarkers: this.querySelector('[ref="progressMarkers"]'),
      progressLabelsTop: this.querySelector('[ref="progressLabelsTop"]'),
      progressDots: this.querySelector('[ref="progressDots"]'),
      savingsBadge: this.querySelector('[ref="savingsBadge"]'),
      savingsBadgeText: this.querySelector('[ref="savingsBadgeText"]'),
      submit: this.querySelector('[ref="submit"]'),
      submitLabel: this.querySelector('[ref="submitLabel"]'),
      submitSep: this.querySelector('[ref="submitSep"]'),
      submitPrices: this.querySelector('[ref="submitPrices"]'),
      oldPrice: this.querySelector('[ref="oldPrice"]'),
      finalPrice: this.querySelector('[ref="finalPrice"]'),
    };

    this.introText = this.refs.progressLabel?.textContent.trim() || '';

    this.#renderMarkers();
    this.#bindEvents();
    this.#update();
  }

  #bindEvents() {
    this.addEventListener('click', (event) => {
      const target = event.target.closest('button');
      if (!target) return;

      const variantId = target.dataset.variantId;

      if (target.classList.contains('pack-card__add')) {
        this.#setQuantity(variantId, 1);
      } else if (target.classList.contains('pack-qty__plus')) {
        this.#changeQuantity(variantId, 1);
      } else if (target.classList.contains('pack-qty__minus')) {
        this.#changeQuantity(variantId, -1);
      } else if (target === this.refs.submit) {
        this.#addPack();
      }
    });
  }

  get #total() {
    let t = 0;
    for (const q of this.selections.values()) t += q;
    return t;
  }

  #changeQuantity(variantId, delta) {
    const current = this.selections.get(variantId) || 0;
    this.#setQuantity(variantId, current + delta);
  }

  #setQuantity(variantId, qty) {
    qty = Math.max(0, qty);

    // Plafond global
    if (this.maxTotal > 0 && qty > (this.selections.get(variantId) || 0)) {
      const others = this.#total - (this.selections.get(variantId) || 0);
      if (others + qty > this.maxTotal) {
        qty = this.maxTotal - others;
      }
    }

    if (qty <= 0) {
      this.selections.delete(variantId);
    } else {
      this.selections.set(variantId, qty);
    }
    this.#renderCard(variantId);
    this.#update();
  }

  #renderCard(variantId) {
    const card = this.querySelector(`.pack-card[data-variant-id="${variantId}"]`);
    if (!card) return;
    const qty = this.selections.get(variantId) || 0;
    const addBtn = card.querySelector('.pack-card__add');
    const qtyEl = card.querySelector('.pack-qty');
    const valueEl = card.querySelector('.pack-qty__value');

    if (qty > 0) {
      card.classList.add('is-selected');
      if (addBtn) addBtn.hidden = true;
      if (qtyEl) qtyEl.hidden = false;
      if (valueEl) valueEl.textContent = qty;
    } else {
      card.classList.remove('is-selected');
      if (addBtn) addBtn.hidden = false;
      // On ne masque le sélecteur que s'il y a un bouton "Ajouter" pour le remplacer.
      // Sans bouton (réglage show_add_button décoché), le sélecteur reste visible à 0.
      if (qtyEl) qtyEl.hidden = !!addBtn;
      if (valueEl) valueEl.textContent = 0;
    }

    // Le "−" ne sert à rien à 0 : on le désactive (visible seulement sans bouton "Ajouter").
    const minusBtn = card.querySelector('.pack-qty__minus');
    if (minusBtn) minusBtn.disabled = qty <= 0;
  }

  /** Désactive les "+" si plafond global atteint */
  #refreshCaps() {
    const capReached = this.maxTotal > 0 && this.#total >= this.maxTotal;
    this.querySelectorAll('.pack-qty__plus, .pack-card__add').forEach((btn) => {
      const card = btn.closest('.pack-card');
      if (card?.dataset.available === 'false') return;
      btn.disabled = capReached;
    });
  }

  /** Palier actif = plus haut palier dont la quantité est atteinte */
  #activeTierIndex() {
    let idx = -1;
    for (let i = 0; i < this.tiers.length; i++) {
      if (this.#total >= this.tiers[i].qty) idx = i;
    }
    return idx;
  }

  #money(cents) {
    const formatted = formatMoney(cents, this.moneyFormat, this.currency);
    // Si le montant est un euro rond, on retire les décimales nulles (29,00€ → 29€)
    if (cents % 100 === 0) {
      return formatted.replace(/([.,])00\b/, '');
    }
    return formatted;
  }

  #renderMarkers() {
    if (this.tiers.length === 0) return;
    const n = this.tiers.length;

    // Cercles + labels centrés dans des segments égaux : (i+0.5)/n
    const posOf = (i) => ((i + 0.5) / n) * 100;

    // Labels saveurs AU-DESSUS du cercle (ex. "2 saveurs", dernier = "4+ saveurs")
    if (this.refs.progressLabelsTop) {
      this.refs.progressLabelsTop.innerHTML = this.tiers
        .map((t, i) => {
          const word = t.qty > 1 ? this.flavorPlural : this.flavorSingular;
          const qtyLabel = i === n - 1 ? `${t.qty}+` : `${t.qty}`;
          return `<span class="pack-progress__top-label" data-tier-qty="${t.qty}" style="inset-inline-start:${posOf(i)}%;">${qtyLabel} ${word}</span>`;
        })
        .join('');
    }

    // Prix par sachet EN DESSOUS du cercle (ex. "27,55€/sachet")
    if (this.refs.progressMarkers) {
      this.refs.progressMarkers.innerHTML = this.tiers
        .map((t, i) => {
          // Prix/sachet : prix saisi en dur si présent, sinon dérivé de la remise %
          const unitPrice = t.unitPrice > 0 ? t.unitPrice : Math.round(this.basePrice * (1 - t.discount / 100));
          // Badge livraison gratuite : si le total de ce palier (prix/sachet × qty) ≥ seuil.
          let freeShip = '';
          if (this.freeShipText && this.freeShipThreshold > 0 && unitPrice * t.qty >= this.freeShipThreshold) {
            freeShip = `<span class="pack-progress__freeship">${this.freeShipText}</span>`;
          }
          return `<span class="pack-progress__marker" data-tier-qty="${t.qty}" style="inset-inline-start:${posOf(i)}%;">${this.#money(unitPrice)}${this.perUnitText}${freeShip}</span>`;
        })
        .join('');
    }
    if (this.refs.progressDots) {
      const check = '<svg class="pack-progress__check" viewBox="0 0 256 256" fill="currentColor" stroke="currentColor" stroke-width="10" stroke-linejoin="round" aria-hidden="true"><path d="M232.49,80.49l-128,128a12,12,0,0,1-17,0l-56-56a12,12,0,1,1,17-17L96,183,215.51,63.51a12,12,0,0,1,17,17Z"/></svg>';
      const fill = '<span class="pack-progress__dot-fill" aria-hidden="true"></span>';
      this.refs.progressDots.innerHTML = this.tiers
        .map((t, i) => `<span class="pack-progress__dot" data-tier-qty="${t.qty}" style="inset-inline-start:${posOf(i)}%;">${fill}${check}</span>`)
        .join('');
    }
  }

  /** Position visuelle (%) du cercle du palier i — doit matcher #renderMarkers (i+0.5)/n */
  #dotPos(i) {
    return ((i + 0.5) / this.tiers.length) * 100;
  }

  /**
   * Remplissage de la barre ALIGNÉ sur les cercles.
   * La barre remplit jusqu'au cercle du dernier palier atteint, puis interpole
   * vers le cercle suivant proportionnellement à la quantité (pas au segment).
   */
  #fillPercent() {
    const n = this.tiers.length;
    if (n === 0) return 0;
    const total = this.#total;
    if (total <= 0) return 0;

    const activeIdx = this.#activeTierIndex(); // dernier palier atteint, -1 si aucun

    if (activeIdx < 0) {
      // Avant le 1er palier : 0 → cercle 0, proportionnel à qty / tier[0].qty
      const ratio = Math.min(1, total / this.tiers[0].qty);
      return this.#dotPos(0) * ratio;
    }

    const startPos = this.#dotPos(activeIdx);
    const next = this.tiers[activeIdx + 1];
    if (!next) {
      // Dernier palier atteint → barre pleine
      return 100;
    }

    // Interpolation cercle actif → cercle suivant selon la quantité
    const span = next.qty - this.tiers[activeIdx].qty;
    const progressed = total - this.tiers[activeIdx].qty;
    const ratio = span > 0 ? Math.min(1, progressed / span) : 1;
    return startPos + (this.#dotPos(activeIdx + 1) - startPos) * ratio;
  }

  #update() {
    this.#refreshCaps();

    const total = this.#total;
    const tierIdx = this.#activeTierIndex();

    // Barre : remplissage par segments égaux (1 palier = 1 segment)
    const pct = this.#fillPercent();
    if (this.refs.progressBar) {
      this.refs.progressBar.style.width = `${pct}%`;
      this.refs.progressBar.classList.toggle('is-complete', tierIdx === this.tiers.length - 1 && total > 0);
    }

    // Marqueurs + cercles débloqués
    this.querySelectorAll('.pack-progress__marker, .pack-progress__dot, .pack-progress__top-label').forEach((el) => {
      const tq = parseInt(el.dataset.tierQty, 10);
      el.classList.toggle('is-unlocked', total >= tq);
    });

    // Label dynamique
    if (this.refs.progressLabel) {
      // Le label affiche uniquement le texte d'intro (pas de message dynamique).
      // Masqué si l'intro est vide.
      if (this.introText.trim() === '') {
        this.refs.progressLabel.hidden = true;
        this.refs.progressLabel.innerHTML = '';
      } else {
        this.refs.progressLabel.hidden = false;
        this.refs.progressLabel.textContent = this.introText;
      }
    }

    // Prix
    let priceBefore = 0;
    for (const card of this.querySelectorAll('.pack-card')) {
      const id = card.dataset.variantId;
      const qty = this.selections.get(id) || 0;
      if (qty > 0) priceBefore += parseInt(card.dataset.price, 10) * qty;
    }
    const tier = tierIdx >= 0 ? this.tiers[tierIdx] : null;
    let priceFinal;
    if (tier && tier.unitPrice > 0) {
      // Prix/sachet en dur → total = prix/sachet × quantité
      priceFinal = tier.unitPrice * total;
    } else {
      const discount = tier ? tier.discount : 0;
      priceFinal = Math.round(priceBefore * (1 - discount / 100));
    }
    const discount = priceBefore > 0 ? Math.round((1 - priceFinal / priceBefore) * 100) : 0;
    const savings = priceBefore - priceFinal;

    // Badge d'économie (dans la ligne du titre) : visible dès qu'une remise est active.
    if (this.refs.savingsBadge) {
      if (discount > 0) {
        if (this.refs.savingsBadgeText) {
          this.refs.savingsBadgeText.textContent = `Économisez ${discount}%`;
        }
        this.refs.savingsBadge.hidden = false;
      } else {
        this.refs.savingsBadge.hidden = true;
      }
    }

    // Bouton
    if (this.refs.submit) {
      this.refs.submit.disabled = total === 0;
    }
    if (total === 0) {
      if (this.refs.submitLabel) this.refs.submitLabel.hidden = false;
      if (this.refs.submitSep) this.refs.submitSep.hidden = true;
      if (this.refs.submitPrices) this.refs.submitPrices.hidden = true;
    } else {
      if (this.refs.submitLabel) this.refs.submitLabel.hidden = false;
      if (this.refs.submitSep) this.refs.submitSep.hidden = false;
      if (this.refs.submitPrices) this.refs.submitPrices.hidden = false;
      if (this.refs.finalPrice) this.refs.finalPrice.textContent = this.#money(priceFinal);
      if (this.refs.oldPrice) {
        if (discount > 0) {
          this.refs.oldPrice.textContent = this.#money(priceBefore);
          this.refs.oldPrice.hidden = false;
        } else {
          this.refs.oldPrice.hidden = true;
        }
      }
    }
  }

  async #addPack() {
    const items = [];
    for (const [variantId, quantity] of this.selections) {
      if (quantity > 0) items.push({ id: Number(variantId), quantity });
    }
    if (items.length === 0) return;

    // Sections du panier pour le morph du drawer
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    this.refs.submit.disabled = true;

    // Notifie le moteur Horizon 4.1.1 (met à jour + ouvre le cart drawer) via CartLinesUpdateEvent.
    const itemCount = items.reduce((s, i) => s + i.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'pack-builder',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const added = await res.json();

      if (added.status) {
        // Erreur Shopify
        this.refs.submit.disabled = false;
        deferredEventPromise.reject(new Error(added.message || 'Add to cart failed'));
        return;
      }

      // cart/add.js renvoie les lignes ajoutées + les sections HTML, mais PAS le cart complet
      // (ni la devise). createCartFromAjaxResponse exige l'objet cart complet → on le récupère via /cart.js.
      const fullCart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart),
        detail: {
          items: added.items,
          source: 'pack-builder',
          sourceId: this.id || 'pack-builder',
          itemCount,
          productId: this.productId,
          // Sections HTML renvoyées par cart/add.js → morph des lignes du panier/drawer.
          sections: added.sections,
          didError: false,
        },
      });

      // Reset après ajout
      this.selections.clear();
      this.querySelectorAll('.pack-card').forEach((c) => this.#renderCard(c.dataset.variantId));
      this.#update();
    } catch (e) {
      this.refs.submit.disabled = false;
      deferredEventPromise.reject(e);
    }
  }
}

if (!customElements.get('pack-builder')) {
  customElements.define('pack-builder', PackBuilder);
}
